import { useCallback, useEffect, useState } from "react";
import { ApiError, readAudit, type Role } from "../api/client";
import { ScopeAndDuration } from "../cb/ScopeAndDuration";
import { AuditReport } from "../report/AuditReport";
import { AuditorSession } from "../session/AuditorSession";
import { AuditPlanScreen } from "./AuditPlan";
import { AuditStepBar } from "./AuditStepBar";
import { IntakeStep } from "./IntakeStep";
import { ReadinessScreen } from "./Readiness";
import { currentStep, reach, type StepKey } from "./steps";

/**
 * One audit, and the six steps of it — the shell both parties work inside.
 *
 * **The step bar lives here rather than on each screen**, because it is a property of being
 * inside an audit rather than of any one step. It is drawn from `audits.state`, which is
 * read once here: the plan, readiness, session and report screens each fetch their own
 * resource and none of them carries the audit's state, so putting the bar on each of them
 * would mean four more reads of the same fact and four chances for them to disagree.
 *
 * **Opening an audit opens the step it has got to** — `currentStep(audits.state)`, the same
 * stop the rail draws as *now*. The list this was reached from says what the audit is
 * waiting on; landing anywhere else makes the reader navigate to the thing they were just
 * told about. Every other step stays one click away on the bar.
 *
 * A fixed landing on Step 2 was the first behaviour here and it was wrong for most audits:
 * a session in progress or a report awaiting signature opened on a determination that was
 * settled weeks earlier, with the work three clicks away and no sign the platform knew
 * where the audit was.
 *
 * **The landing is decided once, by the first read.** Afterwards the step is the reader's,
 * so a certification body that has walked back to Step 1 to revise an intake is not pulled
 * forward again every time a screen reports a change.
 *
 * **Landing is not the same as staying.** One step hands work to another and the shell
 * follows the handover rather than leaving the lead auditor to find the next stop on the
 * rail. Two steps do it, and both are the same shape — an act that brings the *next* step
 * into existence: the approval that completes Step 2 generates the plan, and issuing the
 * plan grants the auditee their access and opens the document checklist. Everywhere else
 * the parties change what a step *says*, not whose turn it is, and the shell stays put.
 *
 * **The state is re-read when a step says it moved.** A revised intake withdraws both
 * approvals and pushes the audit to `reconfirmation`, so the rail behind the revision form
 * would otherwise still show a plan that has just dropped to draft.
 *
 * **Two facts are read, not one.** `audits.state` stops moving at `awaiting_documents` — the
 * planned date arriving writes nothing — so `audits.audit_day` travels with it and the pair
 * is what the rail and the landing are derived from. The date comparison is the server's, and
 * it is the same one the auditee's portal is served: the certification body moving the audit
 * date must not leave the client being offered *Begin audit session* while the auditor's
 * screens still say the audit is waiting for documents.
 */
export function AuditWorkspace({
  auditId,
  role,
  onBack,
  backLabel,
  step,
  onStep,
}: {
  auditId: string;
  role: Role;
  onBack: () => void;
  /** "All audits" for a certification body, "My audits" for a lead auditor. */
  backLabel: string;
  /** The step the address bar names, or null where it names none. */
  step: StepKey | null;
  /** `replace` for the landing, which is a correction and not somewhere Back leads. */
  onStep: (step: StepKey, replace?: boolean) => void;
}) {
  const [state, setState] = useState<string | null>(null);
  /** `audits.audit_day` — the server's reading of the planned date, never the browser's. */
  const [auditDay, setAuditDay] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /*
   * The shell used to carry the auditee's access token from Step 3 to Steps 4 and 5,
   * because the platform stored only its digest and could not answer for it twice.
   *
   * It no longer does. The token is sealed beside the digest and readiness reads it back
   * for as long as the link works, so the screen that draws it asks the server rather than
   * being handed a copy — one fact in one place, and no way for a shell holding yesterday's
   * token to disagree with a re-issue that replaced it.
   */

  const refresh = useCallback(
    () =>
      readAudit(auditId)
        .then((audit) => {
          setState(audit.state);
          setAuditDay(audit.audit_day);
        })
        .catch((cause: unknown) =>
          setError(cause instanceof ApiError ? cause.message : "The audit could not be read."),
        ),
    [auditId],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /*
   * The step on show. The address bar's, when it names one the reader can reach; otherwise
   * the one the audit has actually got to.
   *
   * The fallback answers three things at once: the first render, before the audit has been
   * read; a pasted link naming a step this reader may not have; and a step the audit has
   * since moved past reachability for — a revision drops the plan, so an auditor sitting on
   * Step 3 when it happens is holding a screen that no longer applies.
   */
  const here =
    state === null
      ? null
      : step !== null && reach(step, state, role).reachable
        ? step
        : currentStep(state, auditDay);

  // And the address bar is told, so a reader who lands on a step by derivation can link to
  // it. Replaced rather than pushed: the landing is where they arrived, not somewhere they
  // went, and Back should leave the audit rather than cycle inside it.
  useEffect(() => {
    if (here !== null && here !== step) onStep(here, true);
  }, [here, step, onStep]);

  if (error && state === null) {
    return (
      <p className="problem" role="alert">
        {error}
      </p>
    );
  }
  if (state === null || here === null) return <p className="sub">Reading the audit&hellip;</p>;

  /**
   * A step reports that the audit moved, and where the mover now belongs.
   *
   * **The refresh is awaited before the step changes**, because reachability is derived
   * from `state` and `state` lives here. Setting the step first would measure it against
   * the audit as it stood *before* the move — so a lead auditor whose approval has just
   * generated the plan would be bounced back to Step 2 by the guard below, for the frame
   * between the two, on a state that is already stale.
   */
  const moveTo = async (next: StepKey) => {
    await refresh();
    onStep(next);
  };

  return (
    <>
      <AuditStepBar
        state={state}
        auditDay={auditDay}
        role={role}
        active={here}
        onSelect={onStep}
        /*
          The way out is the bar's own first item now, rather than a link floating above it.
          It leaves the audit and does not step inside it — `onBack`, not `onStep` — which is
          the same thing the floating link did and the reason the label travels with it.
        */
        onBack={onBack}
        backLabel={backLabel}
      />

      {here === "intake" ? (
        <IntakeStep
          auditId={auditId}
          role={role}
          onRevised={() => void moveTo("determination")}
          onBack={onBack}
        />
      ) : here === "determination" ? (
        <ScopeAndDuration
          auditId={auditId}
          role={role}
          onChanged={refresh}
          onStep={(next) => void moveTo(next)}
        />
      ) : here === "plan" ? (
        <AuditPlanScreen
          auditId={auditId}
          onChanged={refresh}
          onStep={onStep}
          /*
            The second handover, and the same shape as the approval that generated the plan:
            issuing it starts Steps 4 and 5 — the auditee has a link, the checklist is live —
            so the lead auditor is taken to the step that just started rather than left on
            the one that just finished. Nothing travels with them: the link is read on the
            step they are arriving at.
          */
          onIssued={() => {
            void moveTo("readiness");
          }}
        />
      ) : here === "readiness" ? (
        <ReadinessScreen auditId={auditId} />
      ) : here === "session" ? (
        <AuditorSession auditId={auditId} onChanged={refresh} />
      ) : (
        <AuditReport auditId={auditId} onChanged={refresh} />
      )}
    </>
  );
}
